<template>
  <MainLayout>
    <v-layout style="padding: 1em" justify-center row fill-height>
      <v-flex>
        <v-card style="height: 100%" light color="#f4f4fa">
          <v-layout column style="height: 100%">
            <!-- Title -->
            <v-card-title row class="align-center">
              <span class="headline flex-none">Staffs</span>
            </v-card-title>

            <!-- Filter -->
            <div class="d-flex flex-xs-column justify-end align-center">
              <!-- Search Field -->
              <v-text-field
                class="mx-4"
                v-model="filters.search"
                label="Search by mobile, name, email, department name "
                clearable
                prepend-icon="search"
                @input="getUsers"
              />

              <!-- Accepted Status -->
              <v-select
                v-if="isHeader"
                class="filter-select mx-4"
                @change="getUsers"
                prepend-icon="account_box"
                v-model="filters.accepted_by_department"
                :items="confirmedOptions"
                label=""
              />

              <!-- Filter Date Range -->
              <div class="flex-none d-flex mx-4">
                <DatePicker
                  v-model="filters.fromDate"
                  clearable
                  label="From Date"
                  @change="getUsers"
                />

                <DatePicker
                  v-model="filters.toDate"
                  clearable
                  label="To Date"
                  @change="getUsers"
                />
              </div>
            </div>

            <!-- Table -->
            <v-container fluid fill-height class="full-height">
              <v-layout style="padding: 0" class="full-height">
                <v-flex sm12 md12 class="full-height scroll">
                  <v-data-table
                    :headers="headers"
                    :items="items"
                    :pagination.sync="pagination"
                    :loading="loading"
                    :rows-per-page-items="[10, 50, 100]"
                    :total-items="pagination.totalItems"
                    @update:pagination="getUsers"
                    class="elevation-1"
                  >
                    <!-- When items = [] -->
                    <template v-slot:no-data>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment "
                            : "No matching records found"
                        }}
                      </div>
                    </template>

                    <!-- When items = undefined -->
                    <template v-slot:no-results>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment "
                            : "Data will be displayed in here"
                        }}
                      </div>
                    </template>

                    <template v-slot:items="props">
                      <td>
                        {{
                          props.item.department
                            ? props.item.department.name
                            : ""
                        }}
                      </td>
                      <td>{{ props.item.name }}</td>
                      <td>{{ props.item.mobile }}</td>
                      <td>{{ props.item.email }}</td>
                      <td>{{ props.item.total_issues }}</td>
                      <td>{{ props.item.created_at | dateTime }}</td>
                      <td>
                        <template
                          v-if="!props.item.accepted_by_department && isHeader"
                        >
                          <!-- Approve Button -->
                          <v-btn
                            title="Approve"
                            icon
                            @click="confirmApproveUser(props.item)"
                          >
                            <v-icon color="success" small>check</v-icon>
                          </v-btn>

                          <!-- Reject/Delete Button -->
                          <v-btn
                            title="Reject"
                            icon
                            @click="confirmRejectUser(props.item)"
                          >
                            <v-icon color="error" small>close</v-icon>
                          </v-btn>
                        </template>

                        <template
                          v-else-if="
                            isAdmin || props.item.accepted_by_department
                          "
                        >
                          <v-btn
                            title="Delete"
                            icon
                            @click="confirmDeleteUser(props.item)"
                          >
                            <v-icon color="error" small>delete</v-icon>
                          </v-btn>
                        </template>
                      </td>
                    </template>
                  </v-data-table>
                </v-flex>
              </v-layout>
            </v-container>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>
  </MainLayout>
</template>

<script>
import moment from "moment";
import api from "@/api";
import { mapState } from "vuex";
import { PLATFORMS } from "@/constants/platforms";
import DatePicker from "@/components/DatePicker";

import { debounce } from "@/utils/Debounce";

export default {
  metaInfo: {
    title: "Staffs",
    meta: [{ name: "Staffs", content: "staffs management" }]
  },

  components: {
    DatePicker
  },

  computed: {
    ...mapState("auth", ["currentUser"]),

    isHeader() {
      return this.currentUser.platform === PLATFORMS.DEPARTMENT;
    },

    isAdmin() {
      return this.currentUser.platform === PLATFORMS.ADMINISTRATION;
    }
  },

  data() {
    return {
      showDialog: false,
      confirmedOptions: [
        {
          text: "Existing Users",
          value: true
        },
        {
          text: "New Users",
          value: false
        }
      ],
      selectedComplaint: undefined,
      datePiker1: false,
      datePiker2: false,
      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sortBy: null,
        totalItems: 0
      },
      filters: {
        search: undefined,
        accepted_by_department: true,
        fromDate: undefined,
        toDate: undefined
      },
      loading: false,
      items: undefined,
      statuses: [],
      categories: [],
      headers: [
        {
          text: "Department",
          sortable: false
        },
        {
          text: "Name",
          sortable: false
        },
        {
          text: "Mobile",
          width: 100,
          sortable: false
        },
        {
          text: "Email",
          sortable: false
        },
        {
          text: "Total Issues",
          width: 50,
          sortable: false
        },
        {
          text: "Created At",
          width: 200,
          sortable: false
        },
        {
          text: "Actions",
          width: 120,
          sortable: false
        }
      ]
    };
  },

  mounted() {
    this.getUsers();
  },

  methods: {
    convertTime(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleTimeString();
    },

    convertDate(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleDateString();
    },

    getUsers: debounce(async function() {
      try {
        this.loading = true;
        this.items = undefined;

        if (this.filters.search === "") {
          this.filters.search = undefined;
        }

        const res = await api.getStaffUsers({
          page_size: this.pagination.rowsPerPage,
          page: this.pagination.page,
          search: this.filters.search || undefined,
          accepted_by_department: this.filters.accepted_by_department,
          start_time: this.filters.fromDate
            ? moment(this.filters.fromDate)
                .startOf("day")
                .format()
            : undefined,
          end_time: this.filters.toDate
            ? moment(this.filters.toDate)
                .endOf("day")
                .format()
            : undefined
        });

        this.loading = false;
        this.items = res.data.data;
        this.pagination.totalItems = res.data.total;
      } finally {
        this.loading = false;
      }
    }, 500),

    confirmApproveUser(user) {
      this.$confirm("Do you want to approve this user?", "Info", {
        confirmButtonText: "Approve",
        cancelButtonText: "Cancel",
        type: "info"
      })
        .then(() => {
          this.approveUser(user);
        })
        .catch(() => {});
    },

    approveUser(user) {
      api
        .approveUser(user.id)
        .then(() => {
          this.$notify.success("User have been approve");
          this.getUsers();
        })
        .catch(e => {
          this.$notify.error(e.message);
        });
    },

    confirmRejectUser(user) {
      this.$confirm("Do you want to reject the user?", "Warning", {
        confirmButtonText: "Reject",
        cancelButtonText: "Cancel",
        type: "warning"
      })
        .then(() => {
          this.deleteUser(user);
        })
        .catch(() => {});
    },

    confirmDeleteUser(user) {
      this.$confirm("Are you sure delete user?", "Warning", {
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        type: "warning"
      })
        .then(() => {
          this.deleteUser(user);
        })
        .catch(() => {});
    },

    deleteUser(user) {
      api
        .deleteUser(user.id)
        .then(() => {
          this.$notify.info("User have been deleted");
          this.getUsers();
        })
        .catch(e => {
          this.$notify.error(e.message);
        });
    }
  }
};
</script>

<style lang="css" scoped>
.full-height {
  flex: 1 1 auto;
  min-height: 0;
}

.scroll {
  overflow-y: auto;
}

.title-text {
  flex: 1;
}

.search-field {
  flex: none;
  width: 25em;
}

.filter-select {
  flex: none;
  margin-left: 0.625em;
  width: 15em;
}
</style>
